import React, { FC, useMemo, useState } from 'react';

import CustomTextField from '../../../common/components/CustomTextField';
import { useUpsertEntity } from '../../../hooks/mutations';
import { useGetAllEchelons, useGetAllEntities } from '../../../hooks/queries';
import CustomSelect, {
  OptionType,
} from '../../../common/components/CustomSelect/CustomSelect';
import { displaySnackBar } from '../../../common/displaySnackbar';
import { useGetEchelon } from '../../../hooks/queries/useEchelon';
import {
  TypeEnseigne,
  UpsertEntityInput,
} from '../../../types/graphql-global-types';
import {
  deformatSIRETSIREN,
  formatSIRET,
  isEmailValid as validateEmail,
  isSIRET,
} from '../../../common/validator';
import CustomPhoneInput from '../../../common/components/CustomPhoneInput';
import { isValidPhoneNumber } from 'react-phone-number-input';
import ConfirmationButtons from '../../ConfirmationButtons/ConfirmationButtons';
import CustomAutoComplete from '../../../common/components/CustomAutoComplete';
import { useEntityInputs } from './useEntityInputs';
import { EchelonName, EchelonPriority } from '../../../common/constants';
import CustomDetailItem from '../../../common/components/CustomDetailItem/CustomDetailItem';
import CustomDialog from '../../../common/components/CustomDialog';
import { CustomDialogProps } from '../../../common/components/CustomDialog/CustomDialog';
import { Grid } from '@material-ui/core';
import { AllEntities_allEntities_entities } from '../../../graphql/entity/types/AllEntities';
import { useMinimalEnseignes } from '../../../hooks/queries/useEnseign';

interface IEntityFormsModal {
  onCancel: () => void;
  onCreate: () => void;
  setIsLoading: (isLoading: boolean) => void;
  entity?: AllEntities_allEntities_entities;
}

const initialEntity: UpsertEntityInput = {
  enseigneId: 0,
  echelonId: 0,
  name: '',
  parentEntityId: 0,
  entityId: 0,
  reseauId: 0,
  adress: '',
  town: '',
  zipCode: '',
  email: '',
  phone: '',
  siret: '',
  idCRM: 0,
};

const EntityFormsModal: FC<IEntityFormsModal & CustomDialogProps> = (props) => {
  const { onCancel, onCreate, setIsLoading } = props;

  const [isSiretValid, setIsSiretValid] = useState(true);
  const [isFinishFillInputs, setIsFinishFillInputs] = useState<boolean>(false);
  const [isEmailValid, setIsEmailValid] = useState<boolean>(true);

  const [entity, setEntity] = useState<UpsertEntityInput>(
    props?.entity
      ? ({
          echelonId: props?.entity?.echelon?.id,
          enseigneId: props?.entity?.enseigne?.id,
          entityId: props?.entity?.id,
          name: props?.entity?.name,
          parentEntityId: props?.entity?.parentEntity?.id ?? -1,
          adress: props?.entity?.adress ?? '',
          town: props?.entity?.town ?? '',
          zipCode: props?.entity?.zipCode ?? '',
          email: props?.entity?.email,
          phone: props?.entity?.phone,
          siret: props?.entity?.siret,
          reseauId: props?.entity?.reseau?.id,
          idCRM: props?.entity?.idCRM,
        } as UpsertEntityInput)
      : initialEntity,
  );

  const [upsertEntity, { loading, client }] = useUpsertEntity();

  const echelon = useGetEchelon({ input: { id: entity?.echelonId ?? 0 } });

  const enseigns = useMinimalEnseignes();
  const echelons = useGetAllEchelons();
  const parentEntity = useGetAllEntities({
    input: {
      where: {
        enseignes: [{ idCC: entity.enseigneId }],
        echelon: { priority: { lt: echelon?.data?.getEchelon?.priority } },
      },
    },
  });
  const reseauEntities = useGetAllEntities({
    input: {
      where: { echelon: { priority: { equals: EchelonPriority.RESEAU } } },
    },
  });

  const enseignOptionsAsc: OptionType[] =
    enseigns?.data?.allEnseignes?.enseignes?.map((enseign) => ({
      label: enseign?.name || '',
      value: enseign?.id ?? -1,
    })) ?? [];

  const parentEntitiesOptions: OptionType[] =
    parentEntity?.data?.allEntities?.entities?.map((entity) => ({
      label: entity?.name || '',
      value: entity?.id ?? -1,
      groupeLabel: entity?.echelon?.name || '',
    })) || [];

  const echelonOptions: OptionType[] = useMemo(() => {
    /**
       *
       * Si je sélectionne une enseigne immo, dans la liste des échelons proposés, je ne devrais pas voir, ou je ne devrais pas pouvoir sélectionner :
       - PRETEUR (car cet échelon est forcément lié à une enseigne dont l'activité est BANQUE)
       - AGENCE CONNECTCREDIT (car cet échelon est forcément lié à l'enseigne CONNECTCREDIT)
       - RESEAU (CROSS-ENSEIGNES) (car cet échelon ne peut pas dépendre d'une enseigne).
       *
       * */
    const enseign = enseigns?.data?.allEnseignes?.enseignes?.find(
      (enseign) => enseign?.id === entity?.enseigneId,
    );
    const echelonPreteur = echelons?.data?.getAllEchelons?.find(
      (echelon) => echelon?.name === EchelonName?.PRETEUR,
    );
    const echelonAgenceConnectCredit = echelons?.data?.getAllEchelons?.find(
      (echelon) => echelon?.name === EchelonName?.AGENCE_CONNECTCREDIT,
    );
    const echelonReseau = echelons?.data?.getAllEchelons?.find(
      (echelon) => echelon?.name === EchelonName?.RESEAU,
    );
    return (
      echelons?.data?.getAllEchelons?.reduce<OptionType[]>((acc, echelon) => {
        if (
          (echelon?.id === echelonPreteur?.id &&
            enseign?.type !== TypeEnseigne?.BANK) ||
          (echelon?.id === echelonAgenceConnectCredit?.id &&
            enseign?.type !== TypeEnseigne?.CONNECTCREDIT) ||
          (echelon?.id === echelonReseau?.id && !!enseign)
        )
          return acc;
        return [
          ...acc,
          {
            label: echelon?.name || '',
            value: echelon.id,
          },
        ];
      }, []) || []
    );
  }, [entity?.enseigneId, echelons, enseigns]);

  const reseauOptions =
    reseauEntities?.data?.allEntities?.entities?.map((entity) => ({
      label: entity?.name || '',
      value: entity?.id ?? -1,
      groupeLabel: entity?.echelon?.name || '',
    })) || [];

  const inputs = useEntityInputs({
    entity,
    echelon: echelon?.data?.getEchelon,
    enseignOptions: enseignOptionsAsc,
    echelonOptions,
    parentEntitiesOptions,
    reseauOptions,
  });

  const handleChange = (key: string, value: string) => {
    if (key in entity) {
      setEntity((prevState) => ({ ...prevState, [key]: value }));
    }
  };

  const handleCreate = () => {
    if (isRequiredInputsValid() && isSiretValid) {
      props?.setIsLoading(true);
      upsertEntity({
        variables: {
          input: {
            ...entity,
            idCRM: entity?.idCRM ? +entity?.idCRM : null,
            parentEntityId:
              entity.parentEntityId === -1 ? undefined : entity.parentEntityId,
          },
        },
      }).then((data) => {
        if (!data?.data?.upsertEntity) return;

        displaySnackBar(client, {
          type: 'SUCCESS',
          message: !entity.entityId
            ? 'Ajouté avec succès'
            : 'Modifié avec succès',
          isOpen: true,
        });

        onCreate();
        setIsLoading(false);
      });
    }
  };

  const isRequiredInputsValid = () => {
    return !inputs.filter((input) => input?.required && !input?.value)?.length;
  };

  const handleSiretBlur = () => {
    setIsSiretValid(isSIRET(deformatSIRETSIREN(entity?.siret || '')));
  };

  const generateDefaultOption = (key: keyof UpsertEntityInput) => {
    switch (key) {
      case 'enseigneId':
        return enseignOptionsAsc.find(
          (option) => option.value === entity.enseigneId,
        );
      case 'parentEntityId':
        return parentEntitiesOptions.find(
          (option) => option.value === entity.parentEntityId,
        );
      case 'reseauId':
        return reseauOptions.find((option) => option.value === entity.reseauId);
      default:
        return echelonOptions.find(
          (option) => option.value === entity.echelonId,
        );
    }
  };

  return (
    <CustomDialog
      {...props}
      loading={!!props?.loading || enseigns.loading || echelons.loading}
      actions={
        <ConfirmationButtons
          handleCancel={onCancel}
          handleConfirm={() => {
            handleCreate();
            setIsFinishFillInputs(true);
          }}
          disableConfirm={loading}
          confirmText={loading ? 'Chargement' : undefined}
        />
      }>
      <Grid container spacing={1}>
        <Grid item xs={12} sm={6}>
          <CustomDetailItem<UpsertEntityInput>
            name="idCC"
            value={entity?.entityId ? entity?.entityId?.toString() : ''}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <CustomDetailItem<UpsertEntityInput>
            name="idCRM"
            mutationVariableName="idCRM"
            setState={setEntity}
            // TO DO: asked in task description but changed later by customer, so don't delete for now
            // editable={isEchelonPreteur || isEchelonAgenceConnectCredit}
            editable={true}
            value={entity?.idCRM ? entity?.idCRM?.toString() : ''}
          />
        </Grid>

        {inputs.map((input) => {
          return (
            <Grid item key={input.key} xs={12} sm={6}>
              {(input.type === 'text' || input.type === 'email') && (
                <CustomTextField
                  label={input.label}
                  value={
                    input.key === 'siret'
                      ? formatSIRET(input.value)
                      : input.value
                  }
                  required={input.required}
                  onBlur={() => {
                    if (input.type === 'email') {
                      setIsEmailValid(
                        validateEmail(
                          (input?.value as string) || 'example@email.com',
                        ),
                      );
                    }
                    if (input.key === 'siret') {
                      if (input.value) handleSiretBlur();
                      else setIsSiretValid(true);
                    }
                  }}
                  error={
                    (isFinishFillInputs && input?.required && !input?.value) ||
                    (input.type === 'email' && !isEmailValid) ||
                    (input.key === 'siret' && !isSiretValid)
                  }
                  helperText={
                    (isFinishFillInputs &&
                      input?.required &&
                      !input?.value &&
                      input.helperText) ||
                    (input.type === 'email' &&
                      !isEmailValid &&
                      input?.helperText) ||
                    (input.key === 'siret' &&
                      !isSiretValid &&
                      input?.helperText) ||
                    ''
                  }
                  onChange={(event) =>
                    handleChange(input.key, event.target.value)
                  }
                />
              )}
              {input?.type === 'tel' && (
                <CustomPhoneInput
                  label={input?.label}
                  required={input?.required}
                  value={input?.value}
                  onChange={(value: any) => handleChange(input?.key, value)}
                  error={
                    isFinishFillInputs &&
                    ((input?.required && !input?.value) ||
                      (input?.value && !isValidPhoneNumber(input?.value)))
                  }
                  helperText={
                    (isFinishFillInputs &&
                      !input?.value &&
                      input?.required &&
                      input.helperText) ||
                    (isFinishFillInputs &&
                      input?.value &&
                      !isValidPhoneNumber(input?.value) &&
                      'Le numéro de téléphone est invalide') ||
                    ''
                  }
                />
              )}
              {input.type === 'select' && (
                <CustomSelect
                  disabled={
                    (entity?.entityId &&
                      (input?.key === 'echelonId' ||
                        input?.key === 'enseigneId')) ||
                    false
                  }
                  label={input.label}
                  value={input.value}
                  required={input.required}
                  errorSelect={
                    isFinishFillInputs && input?.required && !input?.value
                  }
                  helperText={
                    (isFinishFillInputs &&
                      input?.required &&
                      !input?.value &&
                      input.helperText) ||
                    undefined
                  }
                  options={input?.options ?? undefined}
                  optionsgrouped={input?.optionsGrouped ?? undefined}
                  onChange={(e) =>
                    handleChange(input.key, e.target.value as string)
                  }
                />
              )}
              {input.type === 'autoComplete' && (
                <CustomAutoComplete
                  groupBy={(option) => option.groupeLabel as string}
                  label={input.label}
                  options={input.options || []}
                  optionValue={generateDefaultOption(input.key)}
                  onChange={() => {}}
                  onAutoCompleteChange={(option) =>
                    handleChange(input.key, option?.value)
                  }
                  helperText={
                    (isFinishFillInputs &&
                      input?.required &&
                      !input?.value &&
                      input.helperText) ||
                    undefined
                  }
                  required={input.required}
                  disabled={
                    (entity?.entityId &&
                      (input?.key === 'echelonId' ||
                        input?.key === 'enseigneId')) ||
                    false
                  }
                  error={isFinishFillInputs && input?.required && !input?.value}
                />
              )}
            </Grid>
          );
        })}
      </Grid>
    </CustomDialog>
  );
};

export default EntityFormsModal;
